/**
 * CreatePrepFromReviewModal — the GROUP prep-request handoff modal
 * (CLAUDE.md §34.11). Opened from the calendar card modal when a group
 * review reaches "Appointment made" and no linked prep request exists.
 *
 * Creates ONE genuine prep request for the whole review meeting:
 *   - one meeting-level voice note, recorded with the app's existing
 *     VoiceRecorder and attached AS-IS — no transcription and no AI
 *     summary exist anywhere in the app any more (the assistant simply
 *     plays the note);
 *   - one typed notes field per member, in the planner's flat sort
 *     order (individuals by surname, then entities), names snapshotted
 *     from the planner — never from any transcription. Fields optional;
 *   - an appointment date (required — the prep schema needs it and the
 *     assistant's deadline derives from it).
 *
 * "Send" creates the request (normal prep pipeline entry — it counts in
 * prep stats and behaves like any other request, §34.11), then writes
 * prep_request_id to ALL members atomically via
 * updateReviewClientsExpanded. Cancel discards everything — nothing is
 * created (and no audio uploaded) until Send.
 */

import React, { useState } from 'react';
import { Notebook, Building2 } from 'lucide-react';
import VoiceRecorder from '../recorder/VoiceRecorder';
import { getRoleLabel } from '../../auth/authConfig';
import { useAuth } from '../../auth/AuthProvider';
import { MONTH_NAMES } from '../../utils/reviewPlanner';
import { getTodaySASTDateString } from '../../utils/dateHelpers';
import { createPrepRequest } from '../../api/prepRequestsApi';
import { createSystemNote } from '../../api/prepNotesApi';
import { updateReviewClientsExpanded } from '../../api/reviewClientsApi';
import { uploadVoiceNote } from '../../api/storageApi';

export default function CreatePrepFromReviewModal({ card, onClose, onCreated, onToast }) {
  const { user } = useAuth();
  const assistantLabel = getRoleLabel('new_business_assistant');
  const anchor = card.members[0];
  const today = getTodaySASTDateString();

  const [appointmentDate, setAppointmentDate] = useState('');
  const [voiceBlob, setVoiceBlob] = useState(null);
  // Typed notes per member, keyed by review client id.
  const [notesById, setNotesById] = useState({});
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  const setMemberNotes = (id, text) =>
    setNotesById((prev) => ({ ...prev, [id]: text }));

  const handleSend = async () => {
    setError('');
    if (!appointmentDate) {
      setError('Please choose the appointment date — the preparation deadline derives from it.');
      return;
    }
    if (appointmentDate < today) {
      setError('The appointment date must be today or in the future.');
      return;
    }
    setSending(true);
    try {
      // Upload the meeting voice note only now (Cancel must discard
      // everything). Same bucket + signed-URL mechanism as file voice
      // notes; "prep-" prefix keeps the namespace distinct.
      let voiceNoteUrl = null;
      if (voiceBlob) {
        voiceNoteUrl = await uploadVoiceNote(`prep-${crypto.randomUUID()}`, voiceBlob);
      }

      // Member sections — names snapshotted from the planner rows.
      const memberSections = {
        groupLabel: card.label,
        reviewMonth: anchor.reviewMonth,
        reviewYear: anchor.reviewYear,
        voiceNoteUrl,
        sections: card.members.map((m) => ({
          reviewClientId: m.id,
          name: m.displayName,
          isEntity: m.isEntity === true,
          notes: (notesById[m.id] || '').trim(),
          prepared: false,
        })),
      };

      const prep = await createPrepRequest({
        clientName: `${card.label} — Annual Review`,
        appointmentDate,
        tasks: [],
        createdBy: user?.email,
        sourceReviewGroupId: card.groupId,
        memberSections,
      });

      // Creation audit note (CLAUDE.md 26.1) — names who loaded the request.
      // Non-blocking; this path is Adviser-only (review handoff, 34.11).
      try {
        await createSystemNote(prep.id, 'Preparation request loaded by Marinus.');
      } catch (noteErr) {
        console.warn('[prep-create] creation system note failed:', noteErr);
      }

      // Link every member to the meeting's ONE request, atomically.
      const updatedReviews = await updateReviewClientsExpanded(
        card.members.map((m) => m.id),
        { prepRequestId: prep.id }
      );

      onToast &&
        onToast(
          `Prep request sent to ${assistantLabel} — one request, ${card.members.length} member sections`
        );
      onCreated(prep, updatedReviews);
    } catch (err) {
      setError(err.message || 'Could not create the preparation request.');
      setSending(false);
    }
  };

  return (
    <div className="rv-overlay" onClick={onClose}>
      <div className="rv-modal rv-modal-wide" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Create preparation request">
        <h3 className="rv-modal-title">
          <Notebook size={18} /> Create preparation request — {card.label}
        </h3>
        <div style={{ fontSize: '0.78rem', color: 'var(--cp-text-muted)', marginBottom: '1rem' }}>
          Annual Review · {MONTH_NAMES[anchor.reviewMonth]} {anchor.reviewYear} · {card.members.length} clients
          · goes to {assistantLabel} as one prep request
        </div>

        {error && <div className="rv-error">{error}</div>}

        <div className="rv-field">
          <label className="rv-label" htmlFor="rv-prep-appt">Appointment date</label>
          <input
            id="rv-prep-appt"
            className="rv-input"
            type="date"
            value={appointmentDate}
            min={today}
            onChange={(e) => setAppointmentDate(e.target.value)}
          />
        </div>

        <div className="rv-field">
          <label className="rv-label">Meeting voice note (optional)</label>
          {/* Attached as-is — no transcription, no AI summary, no
              auto-filled fields on this path (§34.11). */}
          <VoiceRecorder onRecordingComplete={setVoiceBlob} disabled={sending} />
        </div>

        <div className="rv-field">
          <label className="rv-label">Notes per member (optional)</label>
          {card.members.map((m) => (
            <div key={m.id} style={{ marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.3rem', fontSize: '0.83rem', fontWeight: 600, color: 'var(--cp-primary)' }}>
                {m.isEntity && <Building2 size={13} />}
                {m.displayName}
                {m.isEntity && <span className="rv-tag rv-tag-entity">ENTITY</span>}
              </div>
              <textarea
                className="rv-input"
                rows={2}
                placeholder={`Preparation notes for ${m.displayName}…`}
                value={notesById[m.id] || ''}
                onChange={(e) => setMemberNotes(m.id, e.target.value)}
                disabled={sending}
                style={{ resize: 'vertical' }}
              />
            </div>
          ))}
        </div>

        <div className="rv-modal-actions">
          <button className="rv-btn rv-btn-outline" onClick={onClose} disabled={sending}>
            Cancel
          </button>
          <button className="rv-btn rv-btn-primary" onClick={handleSend} disabled={sending}>
            {sending ? 'Sending…' : `Send to ${assistantLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
}
